import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { useIntl } from 'react-intl';
import { CommentContextProvider } from '../../../contexts/commentContext';
import { DocumentPermissions } from '../shared/hooks/use-document-team-permissions/use-document-team-permissions';
import { EditorSwitch } from '../../../components/document-wizard/document-editor/EditorSwitch';
import { TemplatePageHeader } from './TemplatePageHeader';
import { DocumentSourceEnum } from '../../../components/document-wizard/document-editor/constants/EditorConstats';
import { useDocumentState } from '../../../contexts/DocumentContext';
import { useModals } from '../../../contexts/ModalsContext';
import { PublishTemplateModal } from '../../../components/templates/PublishTemplateModal';
import { TemplateDraftBanner } from '../../../components/templates/TemplateDraftBanner';
import { getTemplatePublishStatus } from '../../../components/templates/templatePublishStatus';
import { PDFJSTemplateViewerContainer } from '../../../components/pdf-viewer/PDFJSTemplateViewerContainer';
import { TemplateType } from '../../../consts/forms-types';
import { getTemplateType } from '../../../shared/templateType';
import { DocumentSubheaderContainer } from '../../../components/document-wizard/document-wizard-header/DocumentSubheaderContainer';
import { DocumentTemplateType } from '../../../components/document-wizard/document.types';
import TemplateEditorSidenav from '../../../components/document-wizard/documentActivitiesAndTemplatesNavigation/templateEditorSidenav';
import {
  CompareRedlineView,
} from '../../../components/document-wizard/document-editor/docx-native/CompareRedlineView';
import type { CompareVersion } from '../../../components/document-wizard/document-editor/docx-native/compareTypes';
import {
  buildRevisionVersions,
  type DealRevisionLike,
} from '../../../components/document-wizard/document-editor/docx-native/dealCompareVersions';
import { useApolloClient, useQuery, useMutation } from '@apollo/client';
import { queriesV2, mutations } from '../../../api';
import { usePermissions } from '../../../auth';
import './TemplatePage.scss';

export type TemplatePageProps = {
  canEdit: boolean;
  canComment: boolean;
  onSave: any;
  documentId: string;
  templateId: string;
  teamId: any;
  permissions: DocumentPermissions;
  onTitleSave: (title: string) => void;
  onTemplateClose: () => void;
  documentTemplateData: any;
  isOrganizationTemplate?: boolean;
  template: DocumentTemplateType;
  templateTitle: string;
  lastUpdated: string;
  isTemplateLoaded: boolean;
  onRefetchTemplate?: () => void;
};

export const TemplatePage = ({
  canEdit,
  canComment,
  onSave,
  documentId,
  teamId,
  permissions,
  onTitleSave,
  onTemplateClose,
  documentTemplateData,
  isOrganizationTemplate,
  templateId,
  template,
  templateTitle,
  lastUpdated,
  isTemplateLoaded,
  onRefetchTemplate,
}: TemplatePageProps) => {
  const { showHeaderFooterEditorModal: isHeaderFooterEditorModalOpen, setShowHeaderFooterEditorModal } = useModals();
  const { formatMessage } = useIntl();
  const { hasPermission, isAdmin } = usePermissions();
  const { state } = useDocumentState();
  const canManageTemplates = isAdmin || hasPermission('manage_templates');
  // Header/footer locks are open to template editors too; the API accepts
  // edit_templates for those two flags only.
  const canLockHeaderFooter = canManageTemplates || !!hasPermission('edit_templates');
  const [activeTab, setActiveTab] = useState<'edit' | 'compare'>('edit');
  const [isPublishModalOpen, setIsPublishModalOpen] = useState(false);
  // DOCX-native: true when the "Published" view is showing the published body
  // read-only IN PLACE (vs the CKEditor slideout).
  const [docxViewPublished, setDocxViewPublished] = useState(false);
  // DOCX-native: true when the "Compare" view (pick-any-two version redline) is
  // active in place of the editor.
  const [docxViewCompare, setDocxViewCompare] = useState(false);
  // DOCX-native autosave reports its save time here so the header "Last Saved"
  // advances WITHOUT refetching the template (a refetch reloads the editor body
  // and interrupts in-progress edits). We use the newer of local/server time.
  const [lastSavedLocal, setLastSavedLocal] = useState<string | null>(null);
  const handleDraftSaved = useCallback(() => setLastSavedLocal(new Date().toISOString()), []);

  // No longer needed: persistent editor instance maintains state across all transitions

  // Local state to track real-time suggestion changes
  const [localHasPendingSuggestions, setLocalHasPendingSuggestions] = useState(false);
  // Local state to track pending changes (gets reset after publish)
  const [localHasPendingChanges, setLocalHasPendingChanges] = useState<boolean | null>(null);

  const isTemplateUploaded = documentTemplateData.source === DocumentSourceEnum.IMPORTED;
  const isTemplateApp = documentTemplateData.source === DocumentSourceEnum.APP;
  const isClauseTemplate = documentTemplateData?.templateType === TemplateType.CLAUSE;
  const isCompareModeEnabled = state.isCompareModeEnabled;
  const isSharedFromWorkspace = documentTemplateData.sharedFromTeam != null;

  // Extract versioning data from documentTemplateData
  // Use local state if set (after publish), otherwise use server data
  const hasPendingChanges =
    localHasPendingChanges !== null ? localHasPendingChanges : documentTemplateData.hasPendingChanges || false;
  // Use local state for real-time updates, only fallback to server data on initial load
  const hasPendingSuggestions = localHasPendingSuggestions;
  const canPublish = hasPendingChanges && !hasPendingSuggestions;
  // V4.0: docx-native templates edit header/footer INLINE in the editor, so the
  // separate Add-Header/Footer modal is redundant and not rendered for them.
  const isDocxNative = documentTemplateData.editorConfiguration?.editor_engine === 'docx_native';
  const publishedTemplate = documentTemplateData.publishedTemplate || documentTemplateData.template;
  // Use draft template content if available, fallback to published template
  const workingTemplate = documentTemplateData.draftTemplate || documentTemplateData.template;

  // DOCX-native Compare view: the versions available to diff. Each resolves to
  // .docx bytes lazily (draft/published are already docx; the retained CKE
  // original is HTML → converted on demand).
  const apolloClient = useApolloClient();

  // Saved-version history for Compare — fetched in a light metadata-only query
  // (no bodies), mirroring the deal editor. Bodies load on demand.
  const { data: templateRevisionsData } = useQuery(
    queriesV2.getDocumentTemplateRevisionsForCompare,
    {
      variables: { documentTemplateId: String(documentId), teamId: teamId ? String(teamId) : undefined },
      skip: !isDocxNative || !documentId,
      fetchPolicy: 'cache-and-network',
    }
  );

  // Loads ONE template revision's stored body on demand (network-only: the
  // metadata query normalizes the same revision entities WITHOUT the heavy
  // `snapshotContent`, so a cache read would miss it).
  const loadTemplateRevisionBody = useCallback(
    async (revisionId: string): Promise<Record<string, any> | null> => {
      const { data } = await apolloClient.query({
        query: queriesV2.getDocumentTemplateRevisionBody,
        variables: { documentTemplateId: String(documentId), teamId: teamId ? String(teamId) : undefined },
        fetchPolicy: 'network-only',
      });
      const rows =
        (data?.getDocumentTemplateById as Record<string, any> | undefined)?.documentRevisions ?? [];
      const row = rows.find((r: any) => String(r.id) === String(revisionId));
      const body: string | undefined = row?.snapshotContent;
      if (!body) return null;
      return { body, body_storage_format: row?.snapshotFormat ?? 'html' };
    },
    [apolloClient, documentId, teamId]
  );

  const [renameVersionMutation] = useMutation(mutations.renameDocumentVersion);
  const handleRenameTemplateVersion = useCallback(
    async (version: { key: string }, name: string) => {
      const revisionId = version.key.replace(/^revision-/, '');
      await renameVersionMutation({ variables: { revisionId, name } });
      await apolloClient.refetchQueries({
        include: [queriesV2.getDocumentTemplateRevisionsForCompare],
      });
    },
    [renameVersionMutation, apolloClient]
  );

  const compareVersions = useMemo(
    () =>
      isDocxNative
        ? buildTemplateCompareVersions(
            workingTemplate,
            publishedTemplate,
            ((templateRevisionsData?.getDocumentTemplateById as Record<string, any> | undefined)
              ?.documentRevisions ?? []).map((r: any) => ({
              id: r.id,
              name: r.name,
              createdAt: r.createdAt,
              authorName: r.creator?.fullName,
              event: r.event,
              hasSnapshot: r.hasSnapshot,
              snapshotFormat: r.snapshotFormat,
            })) as DealRevisionLike[],
            loadTemplateRevisionBody
          )
        : [],
    [isDocxNative, workingTemplate, publishedTemplate, templateRevisionsData, loadTemplateRevisionBody]
  );
  // Template status - active means not hidden
  const isTemplateActive = !documentTemplateData.hidden;
  // Template is published if the template field has actual content (body key)
  // PDF templates are inherently "published" — their content is the uploaded file, not CKEditor body
  const isPublished = isTemplateUploaded || !!(publishedTemplate && publishedTemplate.body);
  const publishStatus = getTemplatePublishStatus({
    isUploaded: isTemplateUploaded,
    hasPublishedVersion: isPublished,
    hasPendingChanges,
  });
  // Each publish writes a "publish" revision, so the newest one dates the
  // published version. Templates published before that have none; no other
  // column records a publish time, so the date is left out rather than guessed.
  const lastPublishedAt = useMemo(() => {
    const revisions = templateRevisionsData?.getDocumentTemplateById?.documentRevisions ?? [];
    return revisions
      .filter((r) => r.event === 'publish' && r.createdAt)
      .reduce<string | null>(
        (latest, r) => (!latest || Date.parse(r.createdAt) > Date.parse(latest) ? r.createdAt : latest),
        null
      );
  }, [templateRevisionsData]);

  // Initialize local suggestion state from template data
  useEffect(() => {
    setLocalHasPendingSuggestions(documentTemplateData.hasPendingSuggestions || false);
  }, [documentTemplateData.hasPendingSuggestions]);


  // Reset local pending changes state when server data changes (e.g., after navigation)
  useEffect(() => {
    // Only reset if server data has actually changed
    if (
      localHasPendingChanges !== null &&
      localHasPendingChanges !== (documentTemplateData.hasPendingChanges || false)
    ) {
      setLocalHasPendingChanges(null);
    }
  }, [documentTemplateData.hasPendingChanges, localHasPendingChanges]);

  // Callback for when suggestions change in the editor
  const handleSuggestionChange = useCallback((suggestionCount: number) => {
    const hasAnySuggestions = suggestionCount > 0;
    setLocalHasPendingSuggestions(hasAnySuggestions);
  }, []);

  // Callback for when document content changes (e.g., accepting/rejecting suggestions)
  const handleDocumentChange = useCallback(() => {
    // Any document change means we now have pending changes
    setLocalHasPendingChanges(true);
  }, []);

  const handleModalClose = () => {
    setShowHeaderFooterEditorModal(false);
    // Refresh template data to pick up any header/footer changes
    if (onRefetchTemplate) {
      setTimeout(onRefetchTemplate, 500);
    }
  };

  const handlePublishSuccess = () => {
    // Immediately update local state to reflect that there are no pending changes
    setLocalHasPendingChanges(false);
    // Refresh template data after successful publish
    // The parent component should handle refetching the template data
    onRefetchTemplate?.();
  };

  const handleStatusChange = () => {
    // Refresh template data after status change
    // The parent component should handle refetching the template data
    handlePublishSuccess();
  };

  const handleRefetchForTimestamp = () => {
    onRefetchTemplate?.();
  };

  const handleTabChange = (tab: 'edit' | 'compare') => {
    setDocxViewPublished(false); // leaving published view
    // DOCX-native Compare = the in-place pick-any-two redline panel.
    if (isDocxNative) setDocxViewCompare(tab === 'compare');
    setActiveTab(tab);
  };

  // DOCX-native "Published" is an IN-PLACE read-only view (swap the editor to
  // the published body), NOT the CKEditor slideout. CKEditor templates keep the
  // slideout.
  const handleViewPublished = () => {
    // "Published" is an IN-PLACE read-only view (swap the editor to the
    // published body). The CKEditor slideout that used to serve non-docx
    // templates went with CKEditor — every template is docx-native now.
    setDocxViewCompare(false); // published + compare are mutually exclusive
    setDocxViewPublished(true);
  };

  // Function to get the correct template content based on mode and permissions
  const getTemplateForMode = (mode: 'edit' | 'compare' | 'published') => {
    // For organization templates viewed by non-admins, show published version
    // Also for shared workspace templates (always read-only), show published version
    if ((isOrganizationTemplate && !canManageTemplates) || isSharedFromWorkspace) {
      return publishedTemplate || template || {};
    }
    // DOCX-native published view → the published body (read-only).
    if (mode === 'published') {
      return publishedTemplate || template || {};
    }
    // For everyone else (editors with permission), show working template
    return workingTemplate || template || {};
  };

  // Determine the appropriate editor mode based on user permissions
  const getEditorMode = (): 'edit' | 'compare' | 'published' => {
    // Organization templates viewed by non-admins should use published mode
    // Shared workspace templates should also use published mode (always read-only)
    if ((isOrganizationTemplate && !canManageTemplates) || isSharedFromWorkspace) {
      return 'published';
    }
    // DOCX-native in-place published view.
    if (docxViewPublished) {
      return 'published';
    }
    // Otherwise use the selected tab mode
    return activeTab;
  };

  const handlePublishFromMessage = () => {
    if (!canManageTemplates || !hasPendingChanges) return;
    setIsPublishModalOpen(true);
  };

  const handlePublishModalSuccess = () => {
    handlePublishSuccess();
    setIsPublishModalOpen(false);
    // Stay on current tab to see the changes
    // The refetch will update the published template content
  };

  return (
    <div className="document-wizard-page">
      <Helmet
        title={formatMessage(
          {
            id: 'title.template-editor',
            defaultMessage: '{title}',
          },
          { title: templateTitle }
        )}
      />
      <div>
        {isTemplateLoaded &&
          !isTemplateUploaded &&
          !isOrganizationTemplate &&
          !isSharedFromWorkspace &&
          canEdit && (
            <TemplateEditorSidenav
              isCollapsible={!isCompareModeEnabled}
              onClose={onTemplateClose}
              permissions={permissions}
              teamId={teamId}
              isClauseTemplate={documentTemplateData?.templateType === TemplateType.CLAUSE}
              isDocxNative={isDocxNative}
              documentId={documentId}
            />
          )}
      </div>
      <div className="document-wizard-page__full-size-layout">
        <div className="document-wizard-page__header-layout">
          <TemplatePageHeader
            canEdit={!isOrganizationTemplate && !isSharedFromWorkspace ? canEdit : false}
            isUploaded={isTemplateUploaded}
            title={templateTitle}
            onClose={onTemplateClose}
            onSaveTitle={onTitleSave}
            templateTitle={templateTitle || 'Template'}
            templateType={
              documentTemplateData?.templateType === TemplateType.CLAUSE
                ? TemplateType.CLAUSE
                : getTemplateType(documentTemplateData)
            }
            teamId={teamId}
            templateId={templateId}
            teamEditPermissions={permissions.hasEditPermission}
            showCloseButton={isTemplateUploaded}
            isRestrictedEditingEnabled={documentTemplateData.editorConfiguration?.restricted_editing}
            isOrganizationTemplate={isOrganizationTemplate}
            isSharedFromWorkspace={isSharedFromWorkspace}
            hasPendingChanges={hasPendingChanges}
            hasPendingSuggestions={hasPendingSuggestions}
            canPublish={canPublish}
            canManageTemplates={canManageTemplates}
            onPublishSuccess={handlePublishSuccess}
            activeTab={activeTab}
            onTabChange={handleTabChange}
            onViewPublished={handleViewPublished}
            isTemplateActive={isTemplateActive}
            onStatusChange={handleStatusChange}
            lastUpdated={
              lastSavedLocal && (!lastUpdated || lastSavedLocal > lastUpdated)
                ? lastSavedLocal
                : lastUpdated
            }
            isPublished={isPublished}
            isDocxNative={isDocxNative}
            isViewingPublished={docxViewPublished}
            isViewingCompare={docxViewCompare}
            lastPublishedAt={lastPublishedAt}
            publishRequestedAt={documentTemplateData.publishRequestedAt}
            publishRequestedByName={documentTemplateData.publishRequestedBy?.fullName}
          />
          <TemplateDraftBanner
            templateId={templateId}
            templateName={templateTitle}
            publishStatus={publishStatus}
            publishRequestedAt={documentTemplateData.publishRequestedAt}
            canEdit={!isOrganizationTemplate && !isSharedFromWorkspace ? canEdit : false}
            canPublish={!!canManageTemplates}
          />
          {(isOrganizationTemplate || isSharedFromWorkspace) && (
            <DocumentSubheaderContainer
              documentId={documentId}
              teamId={teamId}
              organizationName={isOrganizationTemplate && documentTemplateData.team?.organization?.name}
              orgTemplateSubheader={isOrganizationTemplate && !isSharedFromWorkspace}
              workspaceSharedSubheader={isSharedFromWorkspace}
              workspaceName={isSharedFromWorkspace && documentTemplateData.sharedFromTeam?.name}
              isClauseTemplate={documentTemplateData?.templateType === TemplateType.CLAUSE}
            />
          )}
        </div>

        <div className="document-wizard-page__full-size-layout">
          {/* ✅ Single Editor Instance - DocumentEditor Pattern */}

          {documentId && isTemplateApp && isDocxNative && docxViewCompare ? (
            // DOCX-native Compare view — pick-any-two version redline, in place
            // of the editor. (CKEditor compare is unchanged / handled elsewhere.)
            <CompareRedlineView versions={compareVersions} onRenameVersion={handleRenameTemplateVersion} />
          ) : documentId && isTemplateApp ? (
            <CommentContextProvider InitialComments={[]}>
              {/* V4.0: EditorSwitch picks CKEditor (default) or the DOCX-native
                  editor per the template's editor_engine flag. CKEditor path is
                  unchanged; the docx-native path is lazy-loaded (dormant unless
                  flagged). All surrounding template UI (publishing, sidenav,
                  revision switcher) stays the same. */}
              <EditorSwitch
                // Remount on edit↔published switch so the docx-native editor
                // re-resolves the body (it freezes the body on mount to protect
                // in-progress edits; a keyed remount is the clean way to swap
                // between the live draft and the read-only published view).
                key={`editor-${getEditorMode()}`}
                editorEngine={documentTemplateData.editorConfiguration?.editor_engine}
                instanceId="template"
                documentId={documentId}
                save={onSave}
                documentTemplate={getTemplateForMode(getEditorMode())}
                templateTitle={templateTitle || 'Template'}
                teamId={teamId}
                canEdit={!isOrganizationTemplate && !isSharedFromWorkspace ? canEdit : false}
                canComment={canComment}
                canManageTemplates={canManageTemplates}
                canLockHeaderFooter={canLockHeaderFooter}
                permissions={permissions}
                isRestrictedEditingEnabled={documentTemplateData.editorConfiguration?.restricted_editing}
                disableWordExport={documentTemplateData.editorConfiguration?.disable_word_export}
                editorConfiguration={documentTemplateData.editorConfiguration}
                isOrganizationTemplate={isOrganizationTemplate}
                isSharedFromWorkspace={isSharedFromWorkspace}
                organizationName={isOrganizationTemplate && documentTemplateData.team?.organization?.name}
                forceTrackChanges={canEdit && !canManageTemplates}
                onSuggestionChange={handleSuggestionChange}
                onDocumentChange={handleDocumentChange}
                onRefetchTemplate={handleRefetchForTimestamp}
                onDraftSaved={handleDraftSaved}
                editorMode={getEditorMode()} // Pass appropriate mode based on permissions
                isClauseTemplate={isClauseTemplate}
              />
            </CommentContextProvider>
          ) : null}

          {documentId && isTemplateUploaded && (
            <PDFJSTemplateViewerContainer
              templateId={documentId}
              teamId={teamId}
              hasEditPermission={permissions.hasEditTemplatesPermission}
              hasCommentPermission={permissions.hasCommentPermission}
              templateTitle={templateTitle}
              isOrganizationTemplate={isOrganizationTemplate}
              isSharedFromWorkspace={isSharedFromWorkspace}
            />
          )}

          {/* The separate header/footer editor modal was CKEditor-only — note
              the `!isDocxNative` guard it already carried. The DOCX-native
              editor edits headers and footers inline, and now that it is the
              only engine the modal is unreachable, so it went with CKEditor. */}

          <PublishTemplateModal
            isOpen={isPublishModalOpen}
            onClose={() => setIsPublishModalOpen(false)}
            templateId={templateId}
            templateName={templateTitle || 'Template'}
            templateType={documentTemplateData?.templateType === TemplateType.CLAUSE ? TemplateType.CLAUSE : undefined}
            hasPendingSuggestions={hasPendingSuggestions}
            onPublishSuccess={handlePublishModalSuccess}
            isDocxNative={isDocxNative}
          />

        </div>
      </div>
    </div>
  );
};

// ── DOCX-native Compare version sources ──────────────────────────────────────

/** Decode a base64 .docx body → bytes. */
function base64DocxToBytes(b64: string): Uint8Array {
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i += 1) bytes[i] = bin.charCodeAt(i);
  return bytes;
}

/** Resolve a template body-hash to .docx bytes (docx = decode; html = convert). */
async function bodyHashToDocxBytes(bodyHash: Record<string, any> | undefined | null): Promise<Uint8Array | null> {
  const body: string | undefined = bodyHash?.body;
  if (!body) return null;
  const fmt = bodyHash?.body_storage_format;
  if (fmt === 'docx') {
    try {
      return base64DocxToBytes(body);
    } catch {
      return null;
    }
  }
  // html (or legacy): convert on demand via the browser-safe migration converter.
  let html = body;
  try {
    const decoded = atob(body);
    if (/<[a-z!]/i.test(decoded)) html = decoded;
  } catch {
    /* raw html */
  }
  const { convertHtmlToDocx } = await import('../../../utils/docx-migration-v2');
  const { buffer } = await convertHtmlToDocx(html, window.DOMParser);
  return buffer;
}

/**
 * Build the Compare version list for a docx-native template. These are the
 * user-facing template versions — NOT internal migration artifacts (the
 * retained pre-migration copy is a behind-the-scenes safety copy and is
 * intentionally not surfaced here; users have no concept of "conversion").
 * Named/dated snapshots the user creates will be added here going forward.
 */
function buildTemplateCompareVersions(
  workingTemplate: Record<string, any> | undefined,
  publishedTemplate: Record<string, any> | undefined,
  revisions: readonly DealRevisionLike[] = [],
  loadRevisionBody?: (revisionId: string) => Promise<Record<string, any> | null>
): CompareVersion[] {
  const versions: CompareVersion[] = [
    {
      key: 'draft',
      label: 'Current draft',
      getBytes: () => bodyHashToDocxBytes(workingTemplate),
    },
  ];
  // Every saved template snapshot (manual "Save version", "Before import",
  // "Published – …", …), same as the deal editor. Diff-only rows are filtered
  // out by the shared helper; bodies load lazily.
  const revisionVersions = buildRevisionVersions(
    revisions,
    loadRevisionBody,
    bodyHashToDocxBytes
  );
  // Each publish now writes a NAMED "Published – {date}" revision, so that
  // named card in the list IS the published version — no separate synthetic
  // card needed. But templates published BEFORE that feature have a published
  // body with no publish revision; for THOSE only, surface a synthetic
  // "Last published" so their published version is still comparable.
  const hasPublishRevision = revisions.some((r) => r.event === 'publish');
  if (publishedTemplate?.body && !hasPublishRevision) {
    versions.push({
      key: 'published',
      label: 'Last published',
      // NO DATE, deliberately. Publishing only started writing a "Published —
      // {date}" revision recently, and nothing else records when a template was
      // published: `updated_at` is the last change to anything and
      // `draft_updated_at` tracks the draft, so either would be a fabricated
      // publish date on a legal document. Say why the date is missing instead
      // of leaving a blank that reads as a bug.
      sublabel: 'Published before version history was recorded',
      getBytes: () => bodyHashToDocxBytes(publishedTemplate),
    });
  }
  versions.push(...revisionVersions);
  return versions;
}
